import type { OrderOfferView } from '@khaddi/contracts';
import { Button, Text, colors, formatVnd, formatVndCompact, radii } from '@khaddi/ui';
import { View } from 'react-native';
import { RouteLine, kmCaption } from './RouteLine';

export type OfferCardProps = {
  offer: OrderOfferView;
  secondsLeft: number;
  onSkip: () => void;
  onAccept: () => void;
  accepting: boolean;
  error: string | null;
};

const countdownLabel = (seconds: number): string => `Còn ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const SummaryCell = ({ label, value, color = colors.text }: { label: string; value: string; color?: string }) => (
  <View style={{ flex: 1, alignItems: 'center' }}>
    <Text variant="label" style={{ fontSize: 11, fontWeight: '500' }}>
      {label}
    </Text>
    <Text variant="bodyStrong" color={color} style={{ fontSize: 15 }} numeric>
      {value}
    </Text>
  </View>
);

export const OfferCard = ({ offer, secondsLeft, onSkip, onAccept, accepting, error }: OfferCardProps) => {
  const stillBrewing = offer.status === 'new' || offer.status === 'brewing';
  return (
    <View accessibilityRole="summary" aria-label={`Đơn mới ${offer.code}`} style={cardStyle}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <View style={{ backgroundColor: colors.red, borderRadius: radii.pill, paddingHorizontal: 10, paddingVertical: 4 }}>
          <Text variant="label" color={colors.textOnDark} style={{ fontSize: 12, fontWeight: '700' }}>
            Đơn mới · #{offer.code}
          </Text>
        </View>
        <Text variant="bodyStrong" color={colors.danger} style={{ fontSize: 13 }} numeric>
          {countdownLabel(secondsLeft)}
        </Text>
      </View>

      <RouteLine
        pickup={{ caption: kmCaption('Lấy tại', offer.pickup.distanceKm), title: offer.pickup.branchName }}
        dropoff={{ caption: kmCaption('Giao đến', offer.dropoff.distanceKm), title: offer.dropoff.address }}
      />

      <View style={{ flexDirection: 'row', gap: 8, backgroundColor: colors.background, borderRadius: radii.md, padding: 10 }}>
        <SummaryCell label="Phí ship" value={formatVnd(offer.fee)} />
        <SummaryCell label="Số ly" value={`${offer.cupCount} ly`} />
        <SummaryCell
          label="Thanh toán"
          value={offer.prepaid ? 'Đã trả' : `Thu ${formatVndCompact(offer.codAmount)}`}
          color={offer.prepaid ? colors.green : colors.goldText}
        />
      </View>

      {stillBrewing ? (
        <Text variant="caption" style={{ marginTop: -4 }}>
          Quán đang pha · bạn có thể nhận trước và đến quán lấy khi xong
        </Text>
      ) : null}
      {error ? (
        <Text variant="caption" color={colors.danger}>
          {error}
        </Text>
      ) : null}

      <View style={{ flexDirection: 'row', gap: 10 }}>
        <Button label="Bỏ qua" variant="outline" onPress={onSkip} style={{ flex: 1 }} disabled={accepting} />
        <Button label="Nhận đơn" onPress={onAccept} loading={accepting} style={{ flex: 2 }} accessibilityLabel={`Nhận đơn ${offer.code}`} />
      </View>
    </View>
  );
};

const cardStyle = {
  marginTop: -50,
  marginHorizontal: 16,
  backgroundColor: colors.surface,
  borderRadius: radii.xl,
  padding: 16,
  gap: 14,
  borderWidth: 2,
  borderColor: colors.primaryText,
  boxShadow: '0 8px 24px rgba(28, 69, 95,.14)',
} as const;
